import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';
import locationShape from 'react-router/lib/PropTypes';
import React, { PropTypes } from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import AvailabilityDetailActions from '../actions/AvailabilityDetailActions';
import DrawerActions from '../actions/DrawerActions';

import ListPresentationActions from '../actions/ListPresentationActions';
import RecommendationActions from '../actions/RecommendationActions';
import GroupSearchFeedbackActions from '../actions/GroupSearchFeedbackActions';
import SearchActions from '../actions/SearchActions';
import Search from '../components/search/Search';
import { currentUserSelector } from '../selectors/AuthSelector';
import { currentLibrarySelector } from '../selectors/LibrarySelector';
import { searchResultBibSelector } from '../selectors/SearchSelector';
import BasePageContainer from './BasePageContainer';
import DocumentHeadContainer from './DocumentHeadContainer';
import EmptySearchResultContainer from './EmptySearchResultContainer';
import GroupSearchFeedbackContainer from './GroupSearchFeedbackContainer';

const SEARCH_DEBOUNCE_TIME = 100;

export class SearchContainer extends BasePageContainer {
  static fetchInitialData(params, query, store) {
    const view = store.getState().getIn(['search', 'catalogSearch', 'view']);
    return [store.dispatch(SearchActions.performSearch({ ...query, view }))];
  }

  static getBackToTitleKey() {
    return 'search';
  }

  constructor(props) {
    super(props);

    this.performSearch = _debounce(
      (query) => this.props.searchActions.performSearch(query),
      SEARCH_DEBOUNCE_TIME
    );
  }

  componentDidMount() {
    super.componentDidMount();

    // This is used to let the header know it's a search results page
    document.body.classList.add('search-results');
  }

  componentWillUnmount() {
    document.body.classList.remove('search-results');
  }

  shouldSearch(nextProps) {
    const userChanged = this.props.currentUser !== nextProps.currentUser;
    const viewChanged = this.props.currentView !== nextProps.currentView;
    return userChanged || viewChanged || !_isEqual(this.props.location.query, nextProps.location.query);
  }

  resetPageOnViewChange(nextProps) {
    const { router } = this.props;
    const { location } = router;
    const query = nextProps.router.location.query;
    const viewChanged = this.props.currentView !== nextProps.currentView;

    if (viewChanged) {
      router.push({
        ...location,
        query: {
          ...query,
          pagination_page: undefined
        }
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.shouldSearch(nextProps)) {
      this.performSearch({
        ...nextProps.location.query,
        view: nextProps.currentView
      });
    }

    this.resetPageOnViewChange(nextProps);
  }

  render() {
    const {
      activeFilters,
      availabilityDetail,
      branches,
      broadened,
      broadenedSearchCount,
      broadenedSearchType,
      catalogSearchOptions,
      currentView,
      groupedSearchEnabled,
      customEdit,
      didYouMeanQuerySuggestion,
      didYouMeanResultCount,
      drawer,
      featuredContent,
      featuredContentBlockPosition,
      fields,
      intl,
      isFetching,
      pagination,
      query,
      recommendation,
      resultPaneActive,
      results,
      rssURL,
      searchQuery,
      searchType,
      singleBranch,
      sortBys,
      suppress,
      title,
      titleKey,
      zeroResults,
      didNotFindOptions
    } = this.props;

    // Actions
    const { availabilityDetailActions, drawerActions, groupSearchFeedbackActions, listPresentationActions, recommendationActions, searchActions, chatWidgets } = this.props;

    if (zeroResults) {
      return <EmptySearchResultContainer />;
    }

    return (
      <div>
        <DocumentHeadContainer pageTitle={intl.formatMessage({ id: 'searchTitle' })} />
        <GroupSearchFeedbackContainer searchResults={results} />
        <Search
          activeFilters={activeFilters}
          availabilityDetail={availabilityDetail}
          availabilityDetailActions={availabilityDetailActions}
          branches={branches}
          broadened={broadened}
          broadenedSearchCount={broadenedSearchCount}
          broadenedSearchType={broadenedSearchType}
          catalogSearchOptions={catalogSearchOptions}
          currentUser={this.props.currentUser}
          currentView={currentView}
          groupedSearchEnabled={groupedSearchEnabled}
          customEdit={customEdit}
          didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
          didYouMeanResultCount={didYouMeanResultCount}
          drawer={drawer}
          drawerActions={drawerActions}
          featuredContent={featuredContent}
          featuredContentBlockPosition={featuredContentBlockPosition}
          fields={fields}
          groupSearchFeedbackActions={groupSearchFeedbackActions}
          isFetching={isFetching}
          libraryShortName={this.props.libraryShortName}
          listPresentationActions={listPresentationActions}
          location={this.props.location}
          locked={this.props.locked}
          pagination={pagination}
          query={query}
          recommendation={recommendation}
          recommendationActions={recommendationActions}
          resultPaneActive={resultPaneActive}
          results={results}
          rssURL={rssURL}
          searchActions={searchActions}
          searchQuery={searchQuery}
          searchType={searchType}
          showExternalSearchLink={this.props.showExternalSearchLink}
          singleBranch={singleBranch}
          sortBys={sortBys}
          suppress={suppress}
          title={title}
          titleKey={titleKey}
          zeroResults={zeroResults}
          router={this.props.router}
          didNotFindOptions={didNotFindOptions}
          orderedSearchTypes={this.props.orderedSearchTypes}
          chatWidgets={chatWidgets} />
        {this.props.children}
      </div>
    );
  }
}

SearchContainer.propTypes = {
  activeFilters: ImmutablePropTypes.map,
  availabilityDetail: ImmutablePropTypes.map,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  branches: ImmutablePropTypes.list,
  broadened: PropTypes.bool,
  broadenedSearchCount: PropTypes.number,
  broadenedSearchType: PropTypes.string,
  catalogSearchOptions: ImmutablePropTypes.map,
  children: ImmutablePropTypes.map,
  currentUser: ImmutablePropTypes.map,
  currentView: PropTypes.string,
  groupedSearchEnabled: PropTypes.bool.isRequired,
  customEdit: PropTypes.bool,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,
  drawer: ImmutablePropTypes.map,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  featuredContentBlockPosition: PropTypes.number,
  fields: ImmutablePropTypes.list,
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func).isRequired,
  intl: intlShape,
  isFetching: PropTypes.bool,
  libraryName: ImmutablePropTypes.map,
  libraryShortName: PropTypes.string.isRequired,
  listPresentationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  location: PropTypes.shape(locationShape),
  locked: PropTypes.bool,
  pagination: ImmutablePropTypes.map,
  query: PropTypes.string.isRequired,
  recommendation: ImmutablePropTypes.map,
  recommendationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  resultPaneActive: PropTypes.bool,
  results: ImmutablePropTypes.list,
  rssURL: PropTypes.string,
  searchActions: PropTypes.objectOf(PropTypes.func).isRequired,
  searchQuery: ImmutablePropTypes.map,
  searchType: PropTypes.string.isRequired,
  showExternalSearchLink: PropTypes.bool,
  singleBranch: PropTypes.bool,
  sortBys: ImmutablePropTypes.list,
  suppress: PropTypes.bool,
  title: PropTypes.string,
  titleKey: PropTypes.string,
  zeroResults: PropTypes.bool,
  didNotFindOptions: ImmutablePropTypes.map,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object)
};

const mapStateToProps = state => {
  const catalogSearch = state.getIn(['search', 'catalogSearch']);
  const library = currentLibrarySelector(state);
  return {
    activeFilters: catalogSearch.get('activeFilters'),
    availabilityDetail: state.get('availabilityDetail'),
    branches: library.get('branches'),
    broadened: catalogSearch.get('broadened'),
    broadenedSearchCount: catalogSearch.get('broadenedSearchCount'),
    broadenedSearchType: catalogSearch.get('broadenedSearchType'),
    catalogSearchOptions: state.getIn(['search', 'catalogSearchOptions']),
    currentUser: currentUserSelector(state),
    currentView: catalogSearch.get('view'),
    groupedSearchEnabled: catalogSearch.get('groupedSearchEnabled'),
    customEdit: catalogSearch.get('custom_edit'),
    didYouMeanQuerySuggestion: catalogSearch.get('didYouMeanQuerySuggestion'),
    didYouMeanResultCount: catalogSearch.get('didYouMeanResultCount'),
    drawer: state.get('drawer'),
    featuredContentBlockPosition: library.get('featuredContentBlockPosition'),
    fields: catalogSearch.get('fields'),
    isFetching: catalogSearch.get('isFetching'),
    libraryShortName: library.get('shortName'),
    orderedSearchTypes: library.get('orderedSearchTypes', Immutable.List()),
    locked: catalogSearch.get('locked'),
    pagination: catalogSearch.get('pagination'),
    query: catalogSearch.get('query'),
    recommendation: state.get('recommendation'),
    resultPaneActive: catalogSearch.get('resultPaneActive'),
    results: searchResultBibSelector(state),
    rssURL: catalogSearch.get('rssURL'),
    searchType: catalogSearch.get('searchType'),
    showExternalSearchLink: library.get('showExternalSearchLink'),
    singleBranch: library.get('singleBranch'),
    sortBys: catalogSearch.get('sortBys'),
    suppress: catalogSearch.get('suppress'),
    title: catalogSearch.get('title'),
    titleKey: catalogSearch.get('title_key'),
    zeroResults: catalogSearch.get('zeroResults'),
    didNotFindOptions: library.get('didNotFindOptions'),
    chatWidgets: library.get('chatWidgets')
  };
};

const mapDispatchToProps = dispatch => ({
  searchActions: bindActionCreators(SearchActions, dispatch),
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  recommendationActions: bindActionCreators(RecommendationActions, dispatch),
  availabilityDetailActions: bindActionCreators(AvailabilityDetailActions, dispatch),
  listPresentationActions: bindActionCreators(ListPresentationActions, dispatch),
  groupSearchFeedbackActions: bindActionCreators(GroupSearchFeedbackActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SearchContainer));



// WEBPACK FOOTER //
// ./app/containers/SearchContainer.jsx